import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  ListItemButton,
  Popover,
  Typography,
} from '@mui/material';

/**
 * Time slot popover component for selecting reservation time
 * @param {Object} props - Component props
 * @param {boolean} props.open - Popover open state
 * @param {HTMLElement} props.anchorEl - Anchor element for popover
 * @param {Function} props.onClose - Close handler
 * @param {Array} props.openingHours - Available time slots
 * @param {string} props.selectedTime - Currently selected time
 * @param {Function} props.onTimeSelect - Time selection handler
 */
const TimeSlotPopover = ({
  open,
  anchorEl,
  onClose,
  openingHours,
  selectedTime,
  onTimeSelect,
}) => {
  const popoverId = open ? 'time-slot-popover' : undefined;

  const handleTimeSelect = (time) => {
    onTimeSelect(time?.Time, time?.ReservationPoint, time?.Deposit);
    onClose();
  };

  const hasNoAvailableSlots = !openingHours ||
    openingHours.length === 0 ||
    (openingHours[0]?.length === 0 && openingHours[1]?.length === 0);

  return (
    <Popover
      id={popoverId}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <Box
        sx={{
          width: { lg: '400px', md: '400px', sm: '350px', xs: '300px' },
          boxSizing: 'border-box',
          p: 2,
        }}
      >
        <Grid container spacing={2}>
          {hasNoAvailableSlots ? (
            <Box sx={{ p: 1, width: '100%', mt: 1 }}>
              <Typography
                sx={{
                  textAlign: 'center',
                  fontWeight: 600,
                  color: '#BC172F',
                }}
              >
                No Available Time for the selected date
              </Typography>
              <Typography
                sx={{
                  textAlign: 'center',
                  fontWeight: 300,
                  fontSize: '12px',
                }}
              >
                You can try selecting another date
              </Typography>
            </Box>
          ) : (
            <>
              {openingHours[0]?.map((time, index) => (
                <Grid item lg={3} md={3} sm={4} xs={4} key={`morning-${index}`}>
                  <ListItemButton
                    disabled={!time?.IsAvailable}
                    onClick={() => handleTimeSelect(time)}
                    selected={selectedTime === time?.Time}
                    sx={{
                      border: '1px solid #d7d7d7',
                      borderRadius: '8px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                    >
                      {time?.Time}
                    </Typography>
                  </ListItemButton>
                </Grid>
              ))}
              
              {openingHours[1]?.map((time, index) => (
                <Grid item lg={3} md={3} sm={4} xs={4} key={`evening-${index}`}>
                  <ListItemButton
                    disabled={!time?.IsAvailable}
                    onClick={() => handleTimeSelect(time)}
                    selected={selectedTime === time?.Time}
                    sx={{
                      border: '1px solid #d7d7d7',
                      borderRadius: '8px',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                    >
                      {time?.Time}
                    </Typography>
                  </ListItemButton>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </Box>
    </Popover>
  );
};

TimeSlotPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.instanceOf(Element),
  onClose: PropTypes.func.isRequired,
  openingHours: PropTypes.array,
  selectedTime: PropTypes.string,
  onTimeSelect: PropTypes.func.isRequired,
};

export default TimeSlotPopover;
